<template>
  <div class="cad-page">
    <div ref="containerRef" class="pcontainer"></div>
    <SceneControlPanel layout="sidebar" kicker="CAD 测绘" title="控制面板" meta="正交投影">
      <GraphicsBackendControlCard :card="graphicsCard" />
      <CADPanel />
    </SceneControlPanel>
  </div>
</template>

<script setup lang="ts">
import { MOUSE } from 'three'
import { onMounted, onUnmounted, provide, useTemplateRef } from 'vue'
import { CameraMode } from '@sharefe/share3d-engine'
import { createCadFixturePlugins } from '../utils/cadFixturePlugins'
import CADPanel from './CADPanel.vue'
import GraphicsBackendControlCard from './GraphicsBackendControlCard.vue'
import SceneControlPanel from './SceneControlPanel.vue'
import { useExampleGraphicsCard } from '../composables/useExampleGraphicsCard'
import { toGraphicsRuntimeError } from '../utils/graphicsRuntimeError'
import { usePotreeEngine } from '../hooks'

const containerRef = useTemplateRef('containerRef')
const graphicsCard = useExampleGraphicsCard({ presetKey: 'cadFixture' })
const plugins = createCadFixturePlugins()
const { engine, init, dispose, actualBackend } = usePotreeEngine(containerRef, {
  backend: graphicsCard.requestedBackend.value,
  plugins,
  cameraMode: CameraMode.Orthographic,
})

provide('engine', engine)

type NavigationLockableControl = {
  enableRotate?: boolean
  enablePan?: boolean
  mouseButtons?: {
    LEFT?: number | null
    MIDDLE?: number | null
    RIGHT?: number | null
  }
}

const lockCadViewAngle = () => {
  const controlManager = engine.value?.controls
  controlManager?.setMode('map')

  const mapController = controlManager?.getController('map') as
    | NavigationLockableControl
    | undefined
  if (mapController) {
    mapController.enableRotate = false
    mapController.enablePan = true
    if (mapController.mouseButtons) {
      mapController.mouseButtons.LEFT = null
      mapController.mouseButtons.MIDDLE = MOUSE.PAN
      mapController.mouseButtons.RIGHT = null
    }
  }
}

onMounted(async () => {
  graphicsCard.markCreating()
  try {
    await init()
  } catch (error) {
    graphicsCard.markError(toGraphicsRuntimeError(error))
    return
  }
  graphicsCard.markReady(actualBackend.value)

  engine.value?.camera.setPresetView('top')
  lockCadViewAngle()
  await engine.value?.loadCadScene({
    data: JSON.stringify({
      entities: [],
      layers: [],
    }),
    format: 'json',
  })
  engine.value?.activateTool('cad')
})

onUnmounted(() => {
  engine.value?.deactivateTool('cad')
  dispose()
})
</script>

<style scoped>
.cad-page {
  --panel-gap: 0px;
  position: relative;
  width: 100%;
  height: 100vh;
  overflow: hidden;
  display: grid;
  grid-template-columns: minmax(0, 7fr) minmax(0, 3fr);
  gap: var(--panel-gap);
  padding: var(--panel-gap);
  box-sizing: border-box;
  align-items: stretch;
}

.pcontainer {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  position: relative;
}
</style>
